import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { tvShows, categories, genreFilters, regionFilters, yearFilters } from '@/mocks/tv-data';
import type { TvShow } from '@/mocks/tv-data';

const genreLabelMap: Record<string, string> = {
  all: '全部类型',
  romance: '爱情',
  action: '动作',
  comedy: '喜剧',
  suspense: '悬疑',
  costume: '古装',
  urban: '都市',
  fantasy: '奇幻',
};

const regionLabelMap: Record<string, string> = {
  all: '全部地区',
  mainland: '中国大陆',
  'hk-tw': '港台',
  korea: '韩国',
  japan: '日本',
  western: '欧美',
};

const yearLabelMap: Record<string, string> = {
  all: '全部年代',
  '2026': '2026',
  '2025': '2025',
  '2024': '2024',
  '2023': '2023',
  '2022': '2022',
  older: '更早',
};

const categoryLabelMap: Record<string, string> = {
  all: '全部',
  drama: '电视剧',
  movie: '电影',
  anime: '动漫',
  variety: '综艺',
};

function ShowCard({ show }: { show: TvShow }) {
  return (
    <Link
      to={`/article/${show.id}`}
      className="group cursor-pointer block"
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
        <img
          src={show.coverImage}
          alt={show.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {show.isNew && (
            <span className="px-2 py-0.5 rounded-md bg-accent-500 text-white text-[10px] font-bold whitespace-nowrap">
              NEW
            </span>
          )}
          {show.isHot && (
            <span className="px-2 py-0.5 rounded-md bg-primary-500 text-white text-[10px] font-bold whitespace-nowrap">
              HOT
            </span>
          )}
        </div>
        <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/60 text-primary-400 text-xs font-semibold">
          {show.rating}
        </div>
        <div className="absolute bottom-2 left-2">
          <span className="text-[11px] text-white/80 bg-black/50 px-2 py-0.5 rounded-md whitespace-nowrap">
            {show.currentEpisode}/{show.episodeCount}集
          </span>
        </div>
      </div>
      <h3 className="text-sm font-medium text-foreground-200 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
        {show.title}
      </h3>
      <p className="text-xs text-foreground-500 line-clamp-1">{show.cast.slice(0, 3).join(' / ')}</p>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeGenre, setActiveGenre] = useState('all');
  const [activeRegion, setActiveRegion] = useState('all');
  const [activeYear, setActiveYear] = useState('all');
  const [sortBy, setSortBy] = useState<'rating' | 'newest' | 'views'>('rating');

  const categoryLabel = categoryLabelMap[slug] || '全部';

  const filteredShows = useMemo(() => {
    let result = [...tvShows];

    if (slug !== 'all') {
      result = result.filter((s) => s.category === slug);
    }

    if (activeGenre !== 'all') {
      result = result.filter((s) => s.genre.includes(activeGenre));
    }

    if (activeRegion !== 'all') {
      result = result.filter((s) => s.region === activeRegion);
    }

    if (activeYear !== 'all') {
      if (activeYear === 'older') {
        result = result.filter((s) => s.year <= 2022);
      } else {
        result = result.filter((s) => s.year === parseInt(activeYear));
      }
    }

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        result.sort((a, b) => b.year - a.year);
        break;
      case 'views':
        result.sort((a, b) => b.views - a.views);
        break;
    }

    return result;
  }, [slug, activeGenre, activeRegion, activeYear, sortBy]);

  const getFilterLabel = (value: string, map: Record<string, string>) => map[value] || value;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryLabel },
            ]}
          />

          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 mb-2">
              {categoryLabel}大全
            </h1>
            <p className="text-sm text-foreground-500">
              共找到 {filteredShows.length} 部{categoryLabel}资源
            </p>
          </div>

          <div className="flex items-center gap-2 mb-5 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-400 hover:text-foreground-200 border border-background-200/60'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="bg-background-100 rounded-xl border border-background-200/60 p-4 md:p-5 mb-6">
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-price-tag-3-line text-xs text-foreground-500"></i>
                  </div>
                  <span className="text-xs text-foreground-500 font-medium">类型</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {genreFilters.map((g) => (
                    <button
                      key={g.slug}
                      onClick={() => setActiveGenre(g.slug)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        activeGenre === g.slug
                          ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                          : 'bg-background-200/50 text-foreground-500 hover:text-foreground-300 border border-transparent'
                      }`}
                    >
                      {getFilterLabel(g.slug, genreLabelMap)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-global-line text-xs text-foreground-500"></i>
                  </div>
                  <span className="text-xs text-foreground-500 font-medium">地区</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {regionFilters.map((r) => (
                    <button
                      key={r.slug}
                      onClick={() => setActiveRegion(r.slug)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        activeRegion === r.slug
                          ? 'bg-secondary-500/20 text-secondary-400 border border-secondary-500/30'
                          : 'bg-background-200/50 text-foreground-500 hover:text-foreground-300 border border-transparent'
                      }`}
                    >
                      {getFilterLabel(r.slug, regionLabelMap)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-2 flex-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line text-xs text-foreground-500"></i>
                  </div>
                  <span className="text-xs text-foreground-500 font-medium mr-1">年代</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    {yearFilters.map((y) => (
                      <button
                        key={y.slug}
                        onClick={() => setActiveYear(y.slug)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                          activeYear === y.slug
                            ? 'bg-accent-500/20 text-accent-400 border border-accent-500/30'
                            : 'bg-background-200/50 text-foreground-500 hover:text-foreground-300 border border-transparent'
                        }`}
                      >
                        {getFilterLabel(y.slug, yearLabelMap)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500">排序:</span>
                  {[
                    { key: 'rating' as const, label: '评分最高' },
                    { key: 'newest' as const, label: '最新上架' },
                    { key: 'views' as const, label: '播放最多' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        sortBy === opt.key
                          ? 'bg-background-200 text-foreground-200 border border-background-300/60'
                          : 'text-foreground-500 hover:text-foreground-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5 pb-14">
            {filteredShows.map((show) => (
              <ShowCard key={show.id} show={show} />
            ))}
          </div>

          {filteredShows.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-3xl text-foreground-600"></i>
              </div>
              <p className="text-foreground-500">暂无符合条件的影视资源</p>
            </div>
          )}
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.51yingshi.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: categoryLabel,
                },
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </div>
  );
}